/** @jsxRuntime classic */
/** @jsx jsx */
import {css, jsx} from "@emotion/core";
import React, {Fragment} from 'react';
import {getAppState, getBootstrapParams} from "../store/AppState";
import {
    Button,
    Dialog,
    FormGroup,
    InfoIcon,
    Item,
    Radio,
    Select,
    TextInput,
    Toolbar,
    ToolbarSpacer,
    WarningIcon
} from "@ice/components";
import {DatasetEntry} from "../store/ApiImpl";
import {getAppComponent, getMapComponent} from "../store/Accessor";
import {$t, $ts} from "../lang/LangUtil";
import {Dataset} from "../data/Dataset";
import {LayerUtil} from "../map/LayerUtil";
import {DatasetFieldSelect} from "./DatasetFieldSelect";
import {FormContainer} from "./FormContainer";
import {DatasetDefaults, FieldDef, GeoFieldType, LayerType} from "../types";


interface AddDatasetDialogProps {
    entries: DatasetEntry[];
}


interface AddDatasetDialogState {
    currEntryIndex: number;
    dataset: Dataset;
    datasetName: string;
    layerType: LayerType;
    geoFieldType: GeoFieldType;
    fieldId: string;

    typeExists: boolean;
    mergeIntoExisting: boolean;
    mergeIntoExistingDataset: Dataset;
}


export class AddDatasetDialog extends React.Component<AddDatasetDialogProps, AddDatasetDialogState> {

    constructor(props: AddDatasetDialogProps) {
        super(props);
        this.state = this.prepareStateForEntry(0);
    }

    private prepareStateForEntry = (entryIndex: number): AddDatasetDialogState => {
        const entry = this.props.entries[entryIndex];
        const datasetDef = entry.datasetDef;
        const dataset: Dataset = new Dataset(datasetDef, entry.data);
        const defaults: DatasetDefaults = getBootstrapParams().getDatasetDefaults(dataset.getType());
        dataset.setName(this.getNewDatasetName(defaults?.displayName ?? dataset.getName()));

        let layerType: LayerType = "point";
        if (defaults?.layerType) {
            layerType = defaults.layerType ?? layerType;
        }

        let fieldDef: FieldDef = null;
        if (defaults?.field) {
            fieldDef = dataset.getFieldDefById(defaults?.field);
        }
        if (!fieldDef) {
            fieldDef = dataset.getFirstFloatFieldDef() ?? dataset.getFirstIntegerFieldDef();
        }
        const appState = getAppState();

        const datasetsForType = appState.getCompatibleDatasetsWith(dataset);

        return {
            currEntryIndex: entryIndex,
            dataset: dataset,
            datasetName: dataset.getName(),
            layerType: layerType,
            geoFieldType: "geometry",
            fieldId: fieldDef?.id,
            typeExists: datasetsForType.length > 0,
            mergeIntoExisting: false,
            mergeIntoExistingDataset: datasetsForType.length > 0 ? datasetsForType[0] : null
        }
    }

    private close = () => {
        getAppState().clearDatasetEntries();
        getAppComponent().hideDialog();
    }

    private onSkip = () => {
        const entryIndex = this.state.currEntryIndex;
        if (entryIndex < this.props.entries.length - 1) {
            this.setState(this.prepareStateForEntry(entryIndex + 1));
        }
    }

    private add = () => {
        const appState = getAppState();
        const toMerge = this.state.mergeIntoExisting && this.state.mergeIntoExistingDataset;

        let dataset: Dataset;

        if (toMerge) {
            dataset = this.state.mergeIntoExistingDataset;
            dataset.mergeDataset(this.state.dataset);
            getMapComponent().recreateLayers();
        } else {
            dataset = this.state.dataset;
            dataset.setName(this.state.datasetName);
            appState.addDataset(dataset);

            const layerDef = LayerUtil.createLayerDef(dataset.getId(), dataset.getName(), dataset);
            layerDef.type = this.state.layerType;
            layerDef.geoFieldType = this.state.geoFieldType;
            layerDef.config.colorFieldId = this.state.fieldId;
            layerDef.config.sizeFieldId = this.state.fieldId;

            appState.addLayerDef(layerDef);
            getMapComponent().addLayer(layerDef);
        }

        const entryIndex = this.state.currEntryIndex;
        if (entryIndex < this.props.entries.length - 1) {
            getAppComponent().refreshToolbox();
            this.setState(this.prepareStateForEntry(entryIndex + 1));
        } else {
            this.close();
            getAppComponent().refresh();
        }
    }

    private renderExistingDatasetMergeSelection = (): JSX.Element => {
        return (
            <Fragment>
                <Item style={{padding: "0.4em 0"}} intent="info">
                    <InfoIcon size="large" />
                    {$t("datasetexistsquestion")}
                </Item>
                <Radio checked={!this.state.mergeIntoExisting} onChange={() =>this.setState({mergeIntoExisting: false})}>
                    {$t("createnewdataset")}
                </Radio>
                <Radio checked={this.state.mergeIntoExisting} onChange={() => this.setState({mergeIntoExisting: true})}>
                    {$t("addexistdataset")}
                </Radio>
            </Fragment>
        )
    }

    private getNewDatasetName = (nameFromDef: string): string => {
        const nameSet: Set<String> = new Set( getAppState().getDatasets().map(ds => ds.getName()?.toLowerCase()) );
        let name = nameFromDef;
        let index = 1;
        while (nameSet.has(name?.toLowerCase())) {
            name = `${nameFromDef} (${++index})`;
        }
        return name;
    }

    private renderNewDataset = (): JSX.Element => {
        const dataset = this.state.dataset;

        const applicableLayerTypes: LayerType[] = ["point", "cluster"];
        if (dataset.supportsGeojson()) {
            applicableLayerTypes.push("geojson");
        }

        const geoFieldTypes: GeoFieldType[] = ["geometry", "country", "state", "county"];

        return (
            <Fragment>
                <FormGroup inline="center" label={$t("datasetlayer")}>
                    <TextInput defaultValue={this.state.datasetName} onChange={(evt) => this.setState({datasetName: evt.target.value })} />
                </FormGroup>
                <FormGroup inline label={$t("layertype")}>
                    <Select<LayerType>
                        clearable={false}
                        searchable={false}
                        getValue={i => applicableLayerTypes[i]}
                        getOptionLabel={i => $t(applicableLayerTypes[i] as any)}
                        getSelectionLabel={def => $t(def as any)}
                        onChange={layerType => this.setState({layerType: layerType as any})}
                        optionCount={applicableLayerTypes.length}
                        value={this.state.layerType}
                    />
                </FormGroup>
                {
                    this.state.layerType === "geojson" &&
                    <FormGroup inline label={$t("geofield")}>
                        <Select<GeoFieldType>
                            clearable={false}
                            searchable={false}
                            getValue={i => geoFieldTypes[i]}
                            getOptionLabel={i => $t(geoFieldTypes[i] as any)}
                            getSelectionLabel={def => $t(def as any)}
                            onChange={geoFieldType => this.setState({geoFieldType: geoFieldType as any})}
                            optionCount={geoFieldTypes.length}
                            value={this.state.geoFieldType}
                        />
                    </FormGroup>
                }
                <DatasetFieldSelect
                    label={$t("field")}
                    fields={dataset.getFieldDefs()}
                    value={dataset.getFieldDefById(this.state.fieldId)}
                    onFieldSelect={fieldDef => this.setState({fieldId: fieldDef.id})}
                />
            </Fragment>
        )
    }

    private renderMergeDataset = (): JSX.Element => {
        const entryIndex = this.state.currEntryIndex;
        const entry = this.props.entries[entryIndex];

        const sameTypeDatasets = getAppState().getCompatibleDatasetsWith(this.state.dataset);

        return (
            <FormGroup inline label={$t("dataset")}>
                <Select<Dataset>
                    clearable={false}
                    searchable={false}
                    getValue={(i) => sameTypeDatasets[i]}
                    getSelectionLabel={ds => ds.getName()}
                    getOptionLabel={(i) => sameTypeDatasets[i].getName()}
                    onChange={(ds: Dataset) => {
                        this.setState({mergeIntoExistingDataset: ds});
                    }}
                    optionCount={sameTypeDatasets.length}
                    value={this.state.mergeIntoExistingDataset}
                />;
            </FormGroup>
        )
    }

    render = () => {
        const entryIndex = this.state.currEntryIndex;

        const total = this.state.dataset.getSize();
        const valid = this.state.dataset.countGeoFieldDataRows();

        let incompleteMessage = null;

        if (total > valid) {
            incompleteMessage =
                valid > 0 ?
                    $ts("onlysomegeodataquestion", valid, total, Math.trunc(valid * 100 / total)) :
                    $t("nogeodatedetected");
        }

        return (
            <Dialog
                onClose={this.close}
                title={$ts("importxofy", entryIndex + 1, this.props.entries.length)}
                closeControl={true}
                modal={true}
            >
                <div css={css`width: 34rem; overflow: hidden; padding: 1rem`}>
                    <FormContainer>
                        {
                            incompleteMessage &&
                            <Item style={{padding: "0.4em 0"}}  intent={valid > 0 ? "warning" : "danger"}>
                                <WarningIcon size="large" /> {incompleteMessage}
                            </Item>
                        }
                        {
                            this.state.typeExists &&
                            this.renderExistingDatasetMergeSelection()
                        }
                        {
                            (!this.state.typeExists || !this.state.mergeIntoExisting) &&
                            this.renderNewDataset()
                        }
                        {
                            this.state.typeExists && this.state.mergeIntoExisting &&
                            this.renderMergeDataset()
                        }

                        <Toolbar bordered={false}>
                            <ToolbarSpacer />
                            <Button autoFocus onClick={this.close}>{$t("cancel")}</Button>
                            {
                                incompleteMessage && this.props.entries.length > 1 && this.state.currEntryIndex < this.props.entries.length - 1 &&
                                <Button onClick={this.onSkip}>{$t("skip")}</Button>
                            }
                            <Button disabled={!this.state.datasetName?.trim() || valid === 0} onClick={this.add} intent="primary">
                                {$t("add")}
                            </Button>
                        </Toolbar>
                    </FormContainer>
                </div>
            </Dialog>
        );
    }

}
